import Swup from 'swup';
import { isMenuOpen, closeMainMenu } from './nav.js';

// Diagnostic: ?noswup=1 disables Swup entirely so the page is plain navigation.
// Used to isolate whether Swup-related instance stacking / prefetching is
// contributing to iOS memory pressure on the gallery pages.
const NO_SWUP = new URLSearchParams(location.search).has('noswup');

// Stub matches the surface app.js + this module touch (only `.hooks.on(...)`).
const swupStub = { hooks: { on: () => {} } };

export const swup = NO_SWUP ? swupStub : new Swup({
    containers: ['#swup'],
    animationSelector: '#swup',
});

let _menuWasOpen = false;

// Swup treats a click on a link to the URL you're already on as a no-op, so
// `visit:start` never fires and the menu that opened it is left hanging open —
// "View all" while already viewing all did nothing at all. For those clicks,
// just dismiss the menu: you're already looking at the page you asked for.
//
// MUST run in the capture phase. Swup's own (bubble-phase) click handler
// pushes the destination into history *synchronously*, so by bubble time
// location already equals the link target for EVERY menu navigation — a
// bubble-phase version of this check misfired on all of them and force-reloaded
// the page swup had just soft-navigated to (the "everything loads twice" flash).
// Capturing runs us first, while location is still the page being left.
//
// Scoped to the menu deliberately — a same-URL link in page content should keep
// the browser's default behaviour.
document.addEventListener('click', e => {
    if (!isMenuOpen) return;
    const link = e.target.closest('#main-menu a[href]');
    if (!link || link.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey) return;

    const to = new URL(link.href, location.href);
    if (to.origin !== location.origin) return;
    if (to.pathname !== location.pathname || to.search !== location.search) return;

    e.preventDefault(); // swup and the browser both skip a defaultPrevented click
    closeMainMenu();
}, { capture: true });

if (!NO_SWUP) {
    // Track menu state and close it before navigation starts
    swup.hooks.on('visit:start', () => {
        _menuWasOpen = isMenuOpen;
        // closeMainMenu fades the whole menu (incl. any open submenu) out as one,
        // then collapses the <details> once it's hidden — so the submenu doesn't
        // visibly snap shut before the fade.
        if (isMenuOpen) closeMainMenu();
    });

    // When navigating from an open menu: hide main instantly so menu fade
    // is the visible transition, then let swup swap content immediately
    swup.hooks.on('animation:out:start', () => {
        if (!_menuWasOpen) return;
        const main = document.querySelector('#swup');
        if (main) {
            main.style.transition = 'none';
            main.style.opacity    = '0';
        }
    });

    // Restore inline styles before fade-in so CSS transition works
    swup.hooks.on('animation:in:start', () => {
        const main = document.querySelector('#swup');
        if (main) {
            main.style.transition = '';
            main.style.opacity    = '';
        }
    });

    // Sync body class (page-xxx) from fetched page
    swup.hooks.on('content:replace', (visit) => {
        if (visit.to.document) {
            document.body.className = visit.to.document.body.className;
        }
    });
}
